import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
} from "@material-ui/core";
import AndroidIcon from "@material-ui/icons/Android";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import InfoIcon from "@material-ui/icons/Info";
import WarningIcon from "@material-ui/icons/Warning";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  runButton: {
    marginLeft: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
  expandedRow: {
    backgroundColor: theme.palette.grey[50],
  },
  detailsContainer: {
    padding: theme.spacing(2),
  },
  actionChip: {
    margin: theme.spacing(0.5),
  },
  pendingCard: {
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  approveButton: {
    marginRight: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: "center",
    padding: theme.spacing(3),
  },
  statsCard: {
    textAlign: "center",
  },
  statsNumber: {
    fontSize: "2rem",
    fontWeight: "bold",
  },
  statsLabel: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
  },
});

const hebrewLabels = {
  title: "AI סוכן מכירות",
  runAgent: "הרץ סוכן",
  running: "מריץ סוכן...",
  agentRuns: "היסטוריית ריצות",
  pendingApprovals: "ממתין לאישור",
  runId: "מזהה ריצה",
  status: "סטטוס",
  trigger: "טריגר",
  startTime: "זמן התחלה",
  duration: "משך",
  productsAnalyzed: "מוצרים נותחו",
  actionsApplied: "פעולות בוצעו",
  actionsPending: "פעולות ממתינות",
  details: "פרטים",
  product: "מוצר",
  actionType: "סוג פעולה",
  confidence: "ביטחון",
  reasoning: "נימוק",
  approve: "אשר",
  reject: "דחה",
  rejectionReason: "סיבת דחייה",
  cancel: "ביטול",
  statusLabels: {
    running: "רץ",
    completed: "הושלם",
    failed: "נכשל",
    pending: "ממתין",
    approved: "אושר",
    rejected: "נדחה",
  },
  triggerLabels: {
    scheduled: "מתוזמן",
    manual: "ידני",
  },
  actionTypes: {
    price_change: "שינוי מחיר",
    description_update: "עדכון תיאור",
    visibility_change: "שינוי נראות",
    seo_update: "עדכון SEO",
  },
  loading: "טוען נתונים...",
  error: "שגיאה בטעינת הנתונים",
  noRuns: "אין היסטוריית ריצות",
  noPending: "אין פעולות ממתינות לאישור",
  runStarted: "סוכן המכירות התחיל לרוץ",
  approveSuccess: "הפעולה אושרה בהצלחה",
  rejectSuccess: "הפעולה נדחתה",
  tabs: {
    runs: "היסטוריית ריצות",
    pending: "ממתין לאישור",
  },
};

class SalesAgentPanel extends React.Component {
  _isMounted = false;

  state = {
    loading: true,
    error: null,
    runs: [],
    pendingActions: [],
    expandedRunId: null,
    runningAgent: false,
    rejectDialogOpen: false,
    selectedAction: null,
    rejectionReason: "",
    activeTab: 0,
  };

  componentDidMount() {
    this._isMounted = true;
    this.loadData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadData = async () => {
    this.setState({ loading: true, error: null });

    try {
      const adminHeaders = utils.getAdminAuthHeaders();
      const [runsRes, pendingRes] = await Promise.all([
        fetch(`${basePath}/api/v1/admin/sales-agent/runs?limit=20`, {
          headers: adminHeaders,
        }),
        fetch(`${basePath}/api/v1/admin/sales-agent/pending-actions?limit=50`, {
          headers: adminHeaders,
        }),
      ]);

      if (!runsRes.ok || !pendingRes.ok) {
        throw new Error("Failed to fetch agent data");
      }

      const [runsData, pendingData] = await Promise.all([
        runsRes.json(),
        pendingRes.json(),
      ]);

      if (this._isMounted) {
        this.setState({
          loading: false,
          runs: runsData.runs || [],
          pendingActions: pendingData.actions || [],
        });
      }
    } catch (error) {
      console.error("Error loading agent data:", error);
      if (this._isMounted) {
        this.setState({
          loading: false,
          error: error.message,
        });
      }
    }
  };

  handleRunAgent = async () => {
    this.setState({ runningAgent: true });

    try {
      const response = await fetch(`${basePath}/api/v1/admin/sales-agent/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to start agent");
      }

      const data = await response.json();

      alert(`${hebrewLabels.runStarted}\nRun ID: ${data.runId}`);

      if (this._isMounted) {
        this.setState({ runningAgent: false });
      }

      // Reload data after a delay
      setTimeout(() => {
        if (this._isMounted) {
          this.loadData();
        }
      }, 5000);
    } catch (error) {
      console.error("Error running agent:", error);
      alert(`Error: ${error.message}`);
      if (this._isMounted) {
        this.setState({ runningAgent: false });
      }
    }
  };

  handleExpandRun = (runId) => {
    this.setState({
      expandedRunId: this.state.expandedRunId === runId ? null : runId,
    });
  };

  handleApprove = async (action) => {
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/sales-agent/actions/${action._id}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...utils.getAdminAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to approve action");
      }

      alert(hebrewLabels.approveSuccess);
      this.loadData();
    } catch (error) {
      console.error("Error approving action:", error);
      alert(`Error: ${error.message}`);
    }
  };

  handleRejectClick = (action) => {
    this.setState({
      rejectDialogOpen: true,
      selectedAction: action,
      rejectionReason: "",
    });
  };

  handleRejectConfirm = async () => {
    const { selectedAction, rejectionReason } = this.state;

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/sales-agent/actions/${selectedAction._id}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...utils.getAdminAuthHeaders(),
          },
          body: JSON.stringify({ reason: rejectionReason }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reject action");
      }

      alert(hebrewLabels.rejectSuccess);
      if (this._isMounted) {
        this.setState({
          rejectDialogOpen: false,
          selectedAction: null,
          rejectionReason: "",
        });
      }
      this.loadData();
    } catch (error) {
      console.error("Error rejecting action:", error);
      alert(`Error: ${error.message}`);
    }
  };

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue });
  };

  formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("he-IL");
  };

  formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return "-";
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const durationMs = end - start;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  getStatusChip = (status) => {
    const configs = {
      running: { color: "primary", label: hebrewLabels.statusLabels.running },
      completed: { color: "default", label: hebrewLabels.statusLabels.completed },
      failed: { color: "secondary", label: hebrewLabels.statusLabels.failed },
      pending: { color: "default", label: hebrewLabels.statusLabels.pending },
      approved: { color: "primary", label: hebrewLabels.statusLabels.approved },
      rejected: { color: "secondary", label: hebrewLabels.statusLabels.rejected },
    };

    const config = configs[status] || { color: "default", label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  renderRunsTable() {
    const { classes } = this.props;
    const { runs, expandedRunId } = this.state;

    if (!runs || runs.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          {hebrewLabels.noRuns}
        </Typography>
      );
    }

    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{hebrewLabels.runId}</TableCell>
              <TableCell>{hebrewLabels.status}</TableCell>
              <TableCell>{hebrewLabels.trigger}</TableCell>
              <TableCell>{hebrewLabels.startTime}</TableCell>
              <TableCell>{hebrewLabels.productsAnalyzed}</TableCell>
              <TableCell>{hebrewLabels.actionsApplied}</TableCell>
              <TableCell>{hebrewLabels.actionsPending}</TableCell>
              <TableCell>{hebrewLabels.details}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <React.Fragment key={run.runId}>
                <TableRow>
                  <TableCell>
                    <Typography variant="caption">{run.runId}</Typography>
                  </TableCell>
                  <TableCell>{this.getStatusChip(run.status)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={hebrewLabels.triggerLabels[run.trigger] || run.trigger}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {this.formatDate(run.startedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>{run.summary?.totalProductsAnalyzed || 0}</TableCell>
                  <TableCell>{run.summary?.actionsApplied || 0}</TableCell>
                  <TableCell>{run.summary?.actionsPending || 0}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => this.handleExpandRun(run.runId)}
                    >
                      {expandedRunId === run.runId ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
                {expandedRunId === run.runId && (
                  <TableRow className={classes.expandedRow}>
                    <TableCell colSpan={8}>
                      <Box className={classes.detailsContainer}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Card className={classes.statsCard}>
                              <CardContent>
                                <Typography className={classes.statsNumber}>
                                  {run.summary?.lowPerformersFound || 0}
                                </Typography>
                                <Typography className={classes.statsLabel}>
                                  Low Performers Found
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Card className={classes.statsCard}>
                              <CardContent>
                                <Typography
                                  className={classes.statsNumber}
                                  style={{ color: "#4caf50" }}
                                >
                                  {run.summary?.actionsApplied || 0}
                                </Typography>
                                <Typography className={classes.statsLabel}>
                                  Actions Applied
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Card className={classes.statsCard}>
                              <CardContent>
                                <Typography
                                  className={classes.statsNumber}
                                  style={{ color: "#ff9800" }}
                                >
                                  {run.summary?.actionsPending || 0}
                                </Typography>
                                <Typography className={classes.statsLabel}>
                                  Pending Approval
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {run.products && run.products.length > 0 && (
                          <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>
                              Products Worked On:
                            </Typography>
                            {run.products.slice(0, 5).map((p, index) => (
                              <Chip
                                key={index}
                                label={`${p.ean} - ${p.productName}`}
                                size="small"
                                className={classes.actionChip}
                              />
                            ))}
                            {run.products.length > 5 && (
                              <Typography variant="caption" color="textSecondary">
                                +{run.products.length - 5} more
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  renderPendingActions() {
    const { classes } = this.props;
    const { pendingActions } = this.state;

    if (!pendingActions || pendingActions.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          {hebrewLabels.noPending}
        </Typography>
      );
    }

    return (
      <Box>
        {pendingActions.map((action) => {
          const product = action.product;
          const actionData = action.action;

          return (
            <Card key={action._id} className={classes.pendingCard}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      {product?.displayName || product?.name || action.ean}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      EAN: {action.ean} • {product?.brand}
                    </Typography>
                    <Box mt={1}>
                      <Chip
                        size="small"
                        icon={<InfoIcon />}
                        label={
                          hebrewLabels.actionTypes[actionData.type] || actionData.type
                        }
                        color="primary"
                      />
                      <Chip
                        size="small"
                        label={`Confidence: ${(actionData.confidence * 100).toFixed(
                          0
                        )}%`}
                        style={{ marginLeft: 8 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box textAlign="right">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CheckIcon />}
                        className={classes.approveButton}
                        onClick={() => this.handleApprove(action)}
                      >
                        {hebrewLabels.approve}
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() => this.handleRejectClick(action)}
                      >
                        {hebrewLabels.reject}
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                    <Box mt={1}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>{hebrewLabels.reasoning}:</strong> {actionData.reasoning}
                      </Typography>
                      {actionData.newPrice && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>New Price:</strong> ₪{actionData.newPrice} (Current: ₪
                          {product?.price})
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }

  render() {
    const { classes } = this.props;
    const {
      loading,
      error,
      runningAgent,
      rejectDialogOpen,
      rejectionReason,
      activeTab,
    } = this.state;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <Typography className={classes.errorText}>
          {hebrewLabels.error}: {error}
        </Typography>
      );
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Box className={classes.sectionHeader}>
            <div className={classes.sectionTitle}>
              <AndroidIcon color="primary" />
              <Typography variant="h6">{hebrewLabels.title}</Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              startIcon={runningAgent ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              onClick={this.handleRunAgent}
              disabled={runningAgent}
              className={classes.runButton}
            >
              {runningAgent ? hebrewLabels.running : hebrewLabels.runAgent}
            </Button>
          </Box>

          <Divider />

          <Box mt={2}>
            <Tabs value={activeTab} onChange={this.handleTabChange} indicatorColor="primary">
              <Tab label={hebrewLabels.tabs.runs} />
              <Tab label={hebrewLabels.tabs.pending} />
            </Tabs>
          </Box>

          <Box mt={2}>
            {activeTab === 0 && this.renderRunsTable()}
            {activeTab === 1 && this.renderPendingActions()}
          </Box>
        </Paper>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => this.setState({ rejectDialogOpen: false })}>
          <DialogTitle>{hebrewLabels.reject}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={hebrewLabels.rejectionReason}
              type="text"
              fullWidth
              multiline
              rows={3}
              value={rejectionReason}
              onChange={(e) => this.setState({ rejectionReason: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ rejectDialogOpen: false })}>
              {hebrewLabels.cancel}
            </Button>
            <Button onClick={this.handleRejectConfirm} color="secondary">
              {hebrewLabels.reject}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

SalesAgentPanel.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(SalesAgentPanel);
